import axiosInstance from '../utils/axiosConfig';

export interface ClusteringAlgorithm {
  id: number;
  name: string;
  display_name: string;
  description?: string;
  algorithm_type: string;
  default_params?: Record<string, any>;
  is_active: boolean;
}

export interface ClusterRequest {
  algorithm: string;
  parameters?: Record<string, any>;
}

export interface ClusterResponse {
  id: number;
  cluster_id: number;
  name?: string;
  type: string;
  center_lat?: number;
  center_lng?: number;
  store_count: number;
  total_visits: number;
  total_duration_hours: number;
  max_distance_km?: number;
  supervisor_id?: number;
  parameters?: Record<string, any>;
}

export interface WorkerAssignmentRequest {
  strategy: 'balanced' | 'supervisor' | 'proximity';
}

export interface WorkerAssignmentResponse {
  cluster_id: number;
  worker_id: number;
  is_primary: boolean;
  max_stores?: number;
}

export interface StoreInCluster {
  id: number;
  name: string;
  address: string;
  lat?: number;
  lng?: number;
  visit_count: number;
  duration_minutes: number;
  distance_km?: number;
}

export interface WorkerInCluster {
  id: number;
  name: string;
  is_primary: boolean;
  max_stores?: number;
  assigned_stores: number;
  assigned_visits: number;
  assigned_hours: number;
}

export interface DetailedClusterResponse {
  id: number;
  cluster_id: number;
  name?: string;
  type: string;
  center_lat?: number;
  center_lng?: number;
  store_count: number;
  total_visits: number;
  total_duration_hours: number;
  max_distance_km?: number;
  supervisor_id?: number;
  clustering_params?: Record<string, any>;
  stores: StoreInCluster[];
  workers: WorkerInCluster[];
}

export interface PlanClustersResponse {
  plan_id: number;
  clusters: DetailedClusterResponse[];
}

class ClusteringService {
  private baseUrl = '/api/v1/clustering';

  async initializeAlgorithms(): Promise<{ message: string }> {
    const { data } = await axiosInstance.post<{ message: string }>(`${this.baseUrl}/initialize-algorithms`);
    return data;
  }

  async getAlgorithms(): Promise<ClusteringAlgorithm[]> {
    const { data } = await axiosInstance.get<ClusteringAlgorithm[]>(`${this.baseUrl}/algorithms`);
    return data;
  }

  async clusterPlanStores(planId: number, request: ClusterRequest): Promise<ClusterResponse[]> {
    const { data } = await axiosInstance.post<ClusterResponse[]>(
      `${this.baseUrl}/plans/${planId}/cluster`,
      request
    );
    return data;
  }

  async assignWorkersToCluster(planId: number, request: WorkerAssignmentRequest): Promise<WorkerAssignmentResponse[]> {
    const { data } = await axiosInstance.post<WorkerAssignmentResponse[]>(
      `${this.baseUrl}/plans/${planId}/assign-workers`,
      request
    );
    return data;
  }

  async getPlanClusters(planId: number): Promise<PlanClustersResponse> {
    const { data } = await axiosInstance.get<PlanClustersResponse>(
      `${this.baseUrl}/plans/${planId}/clusters`
    );
    return data;
  }

  async deletePlanClusters(planId: number): Promise<{ message: string }> {
    const { data } = await axiosInstance.delete<{ message: string }>(
      `${this.baseUrl}/plans/${planId}/clusters`
    );
    return data;
  }

  async getClusterOptimizationData(clusterId: number, transportType: string = 'car'): Promise<{
    cluster_id: number;
    optimization_data: any;
  }> {
    const { data } = await axiosInstance.get(
      `${this.baseUrl}/clusters/${clusterId}/optimization-data`,
      { params: { transport_type: transportType } }
    );
    return data;
  }

  async optimizeCluster(clusterId: number, request: {
    transport_type: string;
    time_limit_seconds?: number;
    use_time_windows?: boolean;
  }): Promise<{
    cluster_id: number;
    success: boolean;
    message: string;
    optimization_data?: any;
    result?: any;
  }> {
    const { data } = await axiosInstance.post(
      `${this.baseUrl}/clusters/${clusterId}/optimize`,
      request
    );
    return data;
  }

  async optimizeAllClusters(planId: number, request: {
    transport_type: string;
    time_limit_seconds?: number;
    use_time_windows?: boolean;
  }): Promise<{
    plan_id: number;
    clusters_count: number;
    transport_type: string;
    optimization_files: Array<{
      cluster_id: number;
      cluster_name: string;
      file: string;
    }>;
    message: string;
  }> {
    const { data } = await axiosInstance.post(
      `${this.baseUrl}/plans/${planId}/optimize-all-clusters`,
      request
    );
    return data;
  }

  async processOptimizationResult(clusterId: number, result: any): Promise<any> {
    const { data } = await axiosInstance.post(
      `${this.baseUrl}/clusters/${clusterId}/process-result`,
      result
    );
    return data;
  }
}

export default new ClusteringService();